"use client";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { BarChart3, Building, CheckCircle, Home, Sun } from "lucide-react";

interface PropertyScoresProps {
  naturalLightScore?: number;
  homeConditionScore?: number;
  masterkeyScore?: number;
  valueScore?: number;
  locationScore?: number;
}

function ScoreIndicator({
  score,
  label,
  icon: Icon,
}: {
  score: number;
  label: string;
  icon: React.ElementType;
}) {
  // Get color based on score
  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-emerald-500";
    if (score >= 60) return "text-amber-500";
    return "text-red-500";
  };

  // Get background color based on score
  const getScoreBg = (score: number) => {
    if (score >= 80) return "bg-emerald-50";
    if (score >= 60) return "bg-amber-50";
    return "bg-red-50";
  };

  return (
    <div className="flex flex-col items-center gap-3 p-4">
      <div className={cn("p-3 rounded-full", getScoreBg(score))}>
        <Icon className={cn("h-5 w-5", getScoreColor(score))} />
      </div>
      <div className="text-center">
        <div className="text-2xl font-semibold mb-1">{score}</div>
        <div className="text-sm text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

export function PropertyScores({
  naturalLightScore,
  homeConditionScore,
  masterkeyScore,
  valueScore,
  locationScore,
}: PropertyScoresProps) {
  return (
    <Card className="mb-6">
      {masterkeyScore && (
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="bg-blue-50 p-3 rounded-full">
              <CheckCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Overall Score</div>
              <div className="text-2xl font-semibold">{masterkeyScore}</div>
            </div>
            <div className="ml-auto flex-1 max-w-80">
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className="h-2 rounded-full bg-blue-500"
                  style={{ width: `${masterkeyScore}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100">
        {homeConditionScore !== undefined && (
          <ScoreIndicator
            score={homeConditionScore}
            label="Home Condition"
            icon={Home}
          />
        )}
        {naturalLightScore !== undefined && (
          <ScoreIndicator
            score={naturalLightScore}
            label="Natural Light"
            icon={Sun}
          />
        )}
        {valueScore !== undefined && (
          <ScoreIndicator score={valueScore} label="Value" icon={BarChart3} />
        )}
        {locationScore !== undefined && (
          <ScoreIndicator
            score={locationScore}
            label="Location"
            icon={Building}
          />
        )}
      </div>
    </Card>
  );
}
